import { useState, useEffect, useRef } from 'react'
import { Icon } from '@iconify/react'
import { Menu } from '@headlessui/react'
import { Link, useNavigate } from 'react-router-dom'
import UserPool from '../pages/auth/UserPool'
import HeaderLogo from './HeaderLogo'
import { HeaderLabel, navigationPathHeader } from '../utilities/pageConstants'

/**
 * Header component that displays the navigation bar with a user menu.
 * The menu includes profile-related actions such as viewing the profile, changing the password, and logging out.
 *
 * @component
 */
const Header = () => {
    const navigate = useNavigate()
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const currentUser = UserPool.getCurrentUser()
    const username = currentUser ? currentUser.getUsername() : null
    /**
     * Menu items available for the user.
     */
    const menuItems = [
        {
            icon: 'solar:user-broken',
            label: HeaderLabel?.myProfile,
            action: () => {
                setIsMenuOpen(false)
                navigate(navigationPathHeader?.profile)
            }
        },
        {
            icon: 'solar:lock-password-linear',
            label: HeaderLabel?.changePassWord,
            to: navigationPathHeader?.changePassword
        },
        {
            icon: 'solar:logout-2-line-duotone',
            label: HeaderLabel?.logOut,
            action: () => {
                setIsMenuOpen(false)
                logout()
            },
            isDanger: true
        }
    ]

    const menuRef = useRef<HTMLDivElement>(null)
    /**
     * Handles clicks outside the menu to close it.
     * @param {MouseEvent} event - The click event.
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    /**
     * Toggles the user menu open and closed.
     */
    const toggleMenu = () => {
        setIsMenuOpen((prevState) => !prevState)
    }
    /**
     * Logs out the user and redirects them to the sign-in page.
     */
    const logout = () => {
        const user = UserPool.getCurrentUser()
        if (user) {
            user.signOut()
            localStorage.clear()
        }
        window.location.href = navigationPathHeader?.signIn
    }

    return (
        <>
            <HeaderLogo />
            <div className='bg-primary flex items-center justify-end divide-x-2 divide-white space-x-1 px-7 py-2.5'>
                <Menu as='div' className='relative' ref={menuRef}>
                    <div>
                        <Menu.Button className='relative flex px-[8px] py-[5px] items-center' onClick={toggleMenu}>
                            <div className='flex items-center gap-2 text-xl font-medium text-white'>
                                {username}
                                <Icon icon={`prime:chevron-${isMenuOpen ? 'up' : 'down'}`} className='text-white' width='24' height='24' />
                            </div>
                        </Menu.Button>
                    </div>
                    <Menu.Items
                        className={`absolute right-0 px-4 z-10 mt-6 origin-top-right rounded-lg bg-white py-0.5 focus:outline-none shadow-lg ${
                            isMenuOpen ? 'block' : 'hidden'
                        }`}
                    >
                        {menuItems.map((item, index) => (
                            <div key={index} className='flex space-x-2'>
                                <Icon
                                    icon={item?.icon}
                                    height={20}
                                    width={20}
                                    className={`self-center ${item?.isDanger ? 'text-danger' : 'text-secondary'}`}
                                />
                                <Menu.Item>
                                    {item?.to ? (
                                        <Link
                                            to={item?.to}
                                            onClick={() => setIsMenuOpen(false)}
                                            className='flex gap-2 py-2.5 text-md justify-end font-semibold items-center whitespace-nowrap text-secondary'
                                        >
                                            {item.label}
                                        </Link>
                                    ) : (
                                        <span
                                            onClick={item.action}
                                            className={`flex items-center justify-end gap-2 py-2.5 text-md font-semibold cursor-pointer whitespace-nowrap ${
                                                item?.isDanger ? 'text-danger' : 'text-secondary'
                                            }`}
                                        >
                                            {item?.label}
                                        </span>
                                    )}
                                </Menu.Item>
                            </div>
                        ))}
                    </Menu.Items>
                </Menu>
            </div>
        </>
    )
}

export default Header
